// Phase A3-1 — defer non-critical work until the browser is idle.
//
// Wraps `requestIdleCallback` with three safety nets:
//   1. `timeout` guarantees the callback fires even if the browser never goes idle
//      (heavy long tasks, hidden tab throttle, etc.).
//   2. `setTimeout` fallback for browsers without `requestIdleCallback`
//      (Safari < 16.4) and for non-browser environments (SSR, jsdom-without-shim).
//   3. `immediate` escape hatch for unit tests and SSR paths that need the side
//      effect to run synchronously.
//
// The helper returns a cancel function so the caller can abort the pending
// callback (e.g. on React unmount).
//
// Intended consumers: any non-critical 3P initialization that the team wants
// to keep but defer past LCP — e.g. analytics beacons, marketing widgets,
// chat launchers. Do NOT use for PCI-scope code (tokenizer, redactor) — that
// path is owned by Phase A1(c) `lazyInitVaulting`.

const DEFAULT_TIMEOUT_MS = 4000;

export interface DeferUntilIdleOptions {
  /**
   * Max milliseconds to wait for an idle window before forcing the callback.
   * Also used as the `setTimeout` delay when `requestIdleCallback` is unavailable.
   * Default: 4000 (covers LCP + hydration on slow 3G).
   */
  timeout?: number;

  /**
   * Run the callback synchronously and skip the idle wait entirely.
   * Useful for unit tests and any caller that has already determined the
   * environment cannot defer (e.g. `document.readyState === 'complete'`
   * AND user-interaction already happened).
   * Default: false.
   */
  immediate?: boolean;
}

const NOOP = (): void => {
  /* no-op */
};

interface IdleWindow {
  requestIdleCallback?: (
    cb: (deadline: { didTimeout: boolean; timeRemaining: () => number }) => void,
    opts?: { timeout: number }
  ) => number;
  cancelIdleCallback?: (handle: number) => void;
}

/**
 * Run `callback` when the browser is idle (or after `timeout` ms).
 * Returns a cancel function — invoking it aborts the pending callback if it
 * has not yet fired.
 */
export function deferUntilIdle(
  callback: () => void,
  options: DeferUntilIdleOptions = {}
): () => void {
  const { timeout = DEFAULT_TIMEOUT_MS, immediate = false } = options;

  if (immediate) {
    callback();
    return NOOP;
  }

  // SSR / non-browser — silently skip. The caller is responsible for guarding
  // against running this at module top-level on the server.
  if (typeof globalThis === 'undefined' || typeof window === 'undefined') {
    return NOOP;
  }

  const idleWindow = window as unknown as IdleWindow;

  if (typeof idleWindow.requestIdleCallback === 'function') {
    const idleHandle = idleWindow.requestIdleCallback(() => callback(), { timeout });
    return () => {
      if (typeof idleWindow.cancelIdleCallback === 'function') {
        idleWindow.cancelIdleCallback(idleHandle);
      }
    };
  }

  // Fallback: plain setTimeout. The callback will fire after `timeout` ms
  // regardless of whether the browser is busy — this is the safer choice for
  // browsers without rIC because we cannot detect idle windows ourselves.
  const timeoutHandle = setTimeout(() => callback(), timeout);
  return () => clearTimeout(timeoutHandle);
}
